import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { ArrowRight, MapPin, Plane, Clock, CheckCircle2 } from "lucide-react";
import TrustBadges from "../components/TrustBadges";

/**
 * SEO-friendly local landing pages per neighborhood.
 * Routes: /rental/:city — city ∈ { everett, cambridge, east-boston }
 */
const CITIES = {
  everett: {
    name: "Everett",
    titleEn: "Car rental in Everett, MA",
    titlePt: "Aluguel de carros em Everett, MA",
    leadEn:
      "Headquartered on 70 Chelsea St, Movix is the neighbourhood rental — walk-in friendly, Portuguese-speaking team, and rates starting at $24.99/day.",
    leadPt:
      "Sediada na 70 Chelsea St, a Movix é a locadora do bairro — atendimento em português, equipe local e diárias a partir de $28,99.",
    distance: "5 min",
    distanceUnit: "drive from Logan (BOS)",
    distanceUnitPt: "do Logan (BOS) de carro",
    pointsEn: [
      "Walk-in pickup at our Everett lot, no shuttle needed",
      "Free Logan drop-off and pickup for any rental over 3 days",
      "Portuguese-speaking team, Brazilian license accepted",
      "Parking available for your own car during your rental",
    ],
    pointsPt: [
      "Retirada na hora no nosso pátio em Everett, sem shuttle",
      "Entrega e devolução grátis no Logan em locações de 3+ dias",
      "Equipe que fala português, aceitamos CNH brasileira",
      "Estacionamento do seu carro durante a locação",
    ],
    map: "https://www.openstreetmap.org/export/embed.html?bbox=-71.0700%2C42.4000%2C-71.0380%2C42.4180&layer=mapnik&marker=42.4084%2C-71.0537",
    address: "70 Chelsea St, Everett, MA 02149",
  },
  cambridge: {
    name: "Cambridge",
    titleEn: "Car rental near Harvard & MIT (Cambridge, MA)",
    titlePt: "Aluguel de carros perto de Harvard & MIT (Cambridge, MA)",
    leadEn:
      "Renting in Cambridge for a Harvard reunion, MIT visit or moving day? We deliver to Harvard Square, Kendall Square and Porter — usually in under 30 minutes.",
    leadPt:
      "Vai a um evento em Harvard, visitar o MIT ou fazer uma mudança? Entregamos em Harvard Square, Kendall e Porter — normalmente em até 30 minutos.",
    distance: "12 min",
    distanceUnit: "drive from our Everett lot",
    distanceUnitPt: "do nosso pátio em Everett",
    pointsEn: [
      "Door-to-door delivery to Harvard Square, Kendall & Porter",
      "Hourly rentals for moving days (4-hour minimum)",
      "Roof racks available — Kayaks and ski gear, no extra charge",
      "Move-out trucks and minivans on demand",
    ],
    pointsPt: [
      "Entrega porta a porta em Harvard Square, Kendall e Porter",
      "Locação por hora para dia de mudança (mínimo 4h)",
      "Rack de teto disponível — caiaque e ski sem custo extra",
      "Caminhonetes e minivans para mudança sob demanda",
    ],
    map: "https://www.openstreetmap.org/export/embed.html?bbox=-71.1390%2C42.3520%2C-71.0830%2C42.3870&layer=mapnik&marker=42.3736%2C-71.1097",
    address: "Delivery in Harvard Square, Kendall Square & Porter",
  },
  "east-boston": {
    name: "East Boston",
    titleEn: "Car rental in East Boston (steps from Logan)",
    titlePt: "Aluguel de carros em East Boston (perto do Logan)",
    leadEn:
      "Skip the airport counter lines. Movix is minutes from Logan terminals — we meet you at arrivals or deliver to your Eastie hotel.",
    leadPt:
      "Pule a fila do balcão do aeroporto. A Movix fica a minutos do Logan — buscamos você na chegada ou entregamos no seu hotel em Eastie.",
    distance: "8 min",
    distanceUnit: "drive from Logan terminals",
    distanceUnitPt: "dos terminais do Logan",
    pointsEn: [
      "Logan terminal pickup — we meet you at arrivals curb",
      "Drop-off at any Maverick, Jeffries Point or Orient Heights address",
      "Late-night and red-eye flights covered (we're 24/7)",
      "Hotel partnerships at Hilton, Hyatt, Embassy Suites — front-desk handover",
    ],
    pointsPt: [
      "Retirada no terminal do Logan — encontramos você na saída",
      "Devolução em Maverick, Jeffries Point ou Orient Heights",
      "Voos de madrugada cobertos (somos 24/7)",
      "Parceria com Hilton, Hyatt, Embassy Suites — entrega na recepção",
    ],
    map: "https://www.openstreetmap.org/export/embed.html?bbox=-71.0500%2C42.3650%2C-71.0150%2C42.3950&layer=mapnik&marker=42.3760%2C-71.0392",
    address: "Delivery at Logan terminals & East Boston hotels",
  },
};

const Neighborhood = () => {
  const { city } = useParams();
  const { lang, t } = useI18n();
  const data = CITIES[city];

  if (!data) return <Navigate to="/" replace />;

  const isPt = lang === "pt";
  const lead = isPt ? data.leadPt : data.leadEn;
  const points = isPt ? data.pointsPt : data.pointsEn;
  const title = isPt ? data.titlePt : data.titleEn;

  return (
    <div data-testid={`neighborhood-${city}`}>
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
        <p className="text-label text-[#FFB400]">
          <span className="line-accent" />
          <MapPin size={11} className="inline mr-1 -mt-1" />
          {data.name} · MA
        </p>
        <h1
          className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white max-w-4xl"
          data-testid="neighborhood-title"
        >
          {title}
        </h1>
        <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl">
          {lead}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-zinc-400">
          <span className="flex items-center gap-2">
            <Plane size={16} className="text-[#FFB400]" />
            <strong className="text-white text-2xl font-heading">{data.distance}</strong>
            <span>{isPt ? data.distanceUnitPt : data.distanceUnit}</span>
          </span>
          <span className="flex items-center gap-2">
            <Clock size={16} className="text-[#FFB400]" />
            {isPt ? "Atendimento 24/7" : "Open 24/7"}
          </span>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/fleet" className="btn-primary glow-coral" data-testid="neighborhood-cta-fleet">
            {t.hero.ctaSecondary} <ArrowRight size={16} />
          </Link>
          <Link to="/contact" className="btn-outline" data-testid="neighborhood-cta-contact">
            {t.nav.contact}
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <TrustBadges />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-label text-[#FFB400]">
            <span className="line-accent" />
            {isPt ? `Por que Movix em ${data.name}?` : `Why Movix in ${data.name}?`}
          </p>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-white">
            {isPt
              ? "Locadora local, sem letra miúda."
              : "A neighborhood rental with no fine print."}
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            {isPt
              ? "Atendimento humano, preço transparente e zero taxa surpresa. Veja por que motoristas em "
              : "Real human service, honest pricing, zero surprise fees. Here's why drivers in "}
            {data.name}
            {isPt ? " escolhem a Movix:" : " choose Movix:"}
          </p>
        </div>

        <ul className="lg:col-span-7 space-y-4">
          {points.map((p, i) => (
            <li
              key={i}
              className="bg-[#0d0d0d] border border-white/10 p-5 flex items-start gap-3"
              data-testid={`neighborhood-point-${i}`}
            >
              <CheckCircle2 size={20} className="text-[#FFB400] mt-0.5 flex-shrink-0" />
              <span className="text-white leading-relaxed">{p}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-20">
        <p className="text-label text-[#FFB400]">
          <span className="line-accent" />
          {data.name}
        </p>
        <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-white">
          {data.address}
        </h2>
        <div className="mt-8 aspect-[16/8] border border-white/10 overflow-hidden">
          <iframe
            title={`Movix ${data.name} Map`}
            className="w-full h-full"
            style={{ filter: "invert(0.92) hue-rotate(180deg) saturate(0.6) brightness(0.95)" }}
            src={data.map}
          />
        </div>
      </section>
    </div>
  );
};

export default Neighborhood;
